import { getCollection } from "@/api/inventory/getConfig";
import { Button, Group, SegmentedControl, Stack, Title } from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useAppKitAccount } from "@reown/appkit/react";
import { Outlet, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTransition } from "react";
import { z } from "zod";

const searchSchema = z.object({
	filter: z
		.enum(["all", "inventory", "vault", "wallet"])
		.optional()
		.catch("inventory"),
});

export const Route = createFileRoute("/_auth/$collection/nfts")({
	validateSearch: (search: Record<string, unknown>) => {
		if (search?.filter && typeof search.filter === "string") {
			window.localStorage.setItem("filter", search.filter);
		}
		const filter = window.localStorage.getItem("filter");

		return searchSchema.parse({ filter });
	},
	loader: async ({ context, params: { collection } }) => {
		const collectionObj = await getCollection({
			queryClient: context.queryClient,
			collectionAddress: collection,
		});

		return collectionObj;
	},
	component: RouteComponent,
});

function RouteComponent() {
	const collection = Route.useLoaderData();
	const navigate = useNavigate({ from: Route.fullPath });
	const { filter } = Route.useSearch();
	const { address } = useAppKitAccount();
	const [isPending, startTransition] = useTransition();
	const [showIncomplete, setShowIncomplete] = useLocalStorage({
		key: "showIncomplete",
		defaultValue: false,
	});

	return (
		<>
			<Group justify="space-between" mb="lg">
				<Group flex={1} justify="space-between">
					<Title order={3}>{collection.name}s</Title>
					<Button
						variant="light"
						size="xs"
						onClick={() => setShowIncomplete((prev) => !prev)}
					>
						{showIncomplete ? "Show all" : "Show incomplete"}
					</Button>
				</Group>
				<SegmentedControl
					style={{
						flexWrap: "wrap",
					}}
					value={filter}
					onChange={(value) => {
						startTransition(() => {
							navigate({
								to: "",
								search: { filter: value as typeof filter },
								viewTransition: false,
							});
						});
					}}
					data={[
						{ label: "All NFTs", value: "all" },
						{ label: "Inventory NFTs", value: "inventory" },
						{ label: "Vault NFTs", value: "vault" },
						{ label: "Wallet NFTs", value: "wallet", disabled: !address },
					]}
				/>
			</Group>
			<Stack opacity={isPending ? 0.5 : 1} pb="xl">
				<Outlet />
			</Stack>
		</>
	);
}
